/*
 * transfer 穿梭框
 * author zhousheng
 */
import * as React from 'react';
import classnames from 'classnames/dedupe';
import OutlinedClose from 'acud-icon/lib/icons/OutlinedClose';
import { createWorker } from '../_util/createWorker';
import Loading from '../loading';
import Table from '../table';
import TargetList from './TargetList';
import Search from '../search';
const defaultFilterOption = (inputValue, option) => option.title.indexOf(inputValue) > -1;
const Target = (props) => {
    const { prefixCls, displayType, showClearAll = true, filterOption = defaultFilterOption, showSearch, showSearchClear = true, tKeys, setTKeys, loading, setLoading, useWorker, workerLimit = 100000, workerLoading = true, rightStyle, locale, expansionSource, columns, pagination, onSearch, tableRightProps = {}, searchRightProps = {} } = props;
    const targetCls = `${prefixCls}-target`;
    const { targetTitle, searchPlaceholder, targetNoData, targetNoSearchData, clearAll, workerLoadingTip } = locale;
    const [targetSource, setTargetSource] = React.useState([]);
    React.useEffect(() => {
        const tkSet = new Set(tKeys);
        if (useWorker && expansionSource.length > workerLimit && tKeys.length > workerLimit) {
            workerLoading && setLoading(true);
            const tsWorker = createWorker(e => {
                const { expansionSource, tkSet } = e.data;
                const tsArr = expansionSource.filter(data => tkSet.has(data.key)
                    // eslint-disable-next-line max-nested-callbacks
                    || data.group && data.childrenKeys.some(childKey => tkSet.has(childKey)));
                self.postMessage(tsArr);
            });
            tsWorker.addEventListener('message', (event) => {
                setTargetSource(event.data);
                tsWorker.terminate();
                workerLoading && setLoading(false);
            });
            tsWorker.postMessage({ expansionSource, tkSet });
        }
        else {
            const tsArr = expansionSource.filter(data => tkSet.has(data.key)
                || data.group && data.childrenKeys.some(childKey => tkSet.has(childKey)));
            setTargetSource(tsArr);
        }
    }, [expansionSource, tKeys]);
    // 搜索关键字
    const [searchValue, setSearchValue] = React.useState('');
    const filterSource = React.useMemo(() => {
        if (searchValue === '') {
            return targetSource;
        }
        const tfs = targetSource.filter(data => {
            if (data.group) {
                return filterOption(searchValue, data)
                    || data.children.some(child => tKeys.includes(child.key) && filterOption(searchValue, child));
            }
            if (data.groupOption) {
                const parentData = targetSource.find(cData => cData.key === data.groupKey);
                return filterOption(searchValue, data)
                    || filterOption(searchValue, parentData);
            }
            return filterOption(searchValue, data);
        });
        return tfs;
    }, [searchValue, targetSource, filterOption]);
    // 已选项
    const optionSource = React.useMemo(() => {
        return filterSource.filter(data => !data.group);
    }, [filterSource]);
    // 已选中的可操作的key
    const totalUndisabledKeys = React.useMemo(() => {
        const unDisabledTargetSource = optionSource.filter(data => !data.disabled);
        return unDisabledTargetSource.map(data => data.key);
    }, [optionSource]);
    // 已选中的不可操作的key
    const totalDisabledKeys = React.useMemo(() => {
        const unDisabledTargetSource = optionSource.filter(data => data.disabled);
        return unDisabledTargetSource.map(data => data.key);
    }, [optionSource]);
    const handleClearAll = React.useCallback(() => {
        const tukSet = new Set(totalUndisabledKeys);
        if (useWorker && tKeys.length > workerLimit && totalUndisabledKeys.length > workerLimit) {
            workerLoading && setLoading(true);
            const tkWorker = createWorker(e => {
                const { tukSet, tKeys } = e.data;
                const newTKeys = tKeys.filter(key => !tukSet.has(key));
                self.postMessage(newTKeys);
            });
            tkWorker.addEventListener('message', (event) => {
                setTKeys(event.data);
                tkWorker.terminate();
                workerLoading && setLoading(false);
            });
            tkWorker.postMessage({ tukSet, tKeys });
        }
        else {
            // const newTKeys = tKeys.filter(key => !totalUndisabledKeys.includes(key));
            const newTKeys = tKeys.filter(key => !tukSet.has(key));
            setTKeys(newTKeys);
        }
    }, [totalUndisabledKeys, tKeys, useWorker, workerLimit, workerLoading]);
    const searchChange = React.useCallback(e => {
        const searchValue = e.target.value;
        setSearchValue(searchValue);
        onSearch === null || onSearch === void 0 ? void 0 : onSearch('right', searchValue);
    }, [onSearch]);
    const targetColumns = React.useMemo(() => {
        if (!columns) {
            return;
        }
        const cancelItem = data => {
            if (data.disabled) {
                return;
            }
            let curTKeys = [...tKeys];
            if (data.group) {
                const deleteKeys = data.childrenKeys;
                curTKeys = curTKeys.filter(tKey => !deleteKeys.includes(tKey));
            }
            else if (curTKeys.includes(data.key)) {
                curTKeys.splice(curTKeys.indexOf(data.key), 1);
            }
            setTKeys(curTKeys);
        };
        const tColumns = [...columns];
        tColumns.push({
            title: '',
            key: 'action',
            width: 40,
            render: (_text, data) => {
                if (!data.disabled) {
                    return <OutlinedClose className={`${prefixCls}-target-item-cancel`} onClick={() => cancelItem(data)}/>;
                }
                return null;
            }
        });
        return tColumns;
    }, [columns, tKeys]);
    const clearCls = classnames({
        [`${targetCls}-clear-all`]: true,
        [`${targetCls}-clear-all-disabled`]: filterSource.length === totalDisabledKeys.length
    });
    return (<Loading loading={loading} tip={workerLoadingTip} size="small">
            <div className={targetCls} style={rightStyle}>
                <div className={`${targetCls}-header`}>
                    <span className={`${targetCls}-label`}>{targetTitle}（{optionSource.length}）</span>
                    {showClearAll && (<span className={clearCls} aria-disabled={optionSource.length === totalDisabledKeys.length} onClick={handleClearAll}>
                            {clearAll}
                        </span>)}
                </div>
                <div className={`${targetCls}-body`}>
                    {(showSearch === true || showSearch === 'right') && <Search placeholder={searchPlaceholder} className={`${targetCls}-search`} allowClear={showSearchClear} size="small" onChange={searchChange} {...searchRightProps}/>}
                    {displayType === 'table' && <div className={`${prefixCls}-table-box`}>
                        <Table pagination={pagination ? Object.assign({ total: optionSource.length }, pagination) : false} dataSource={filterSource} columns={targetColumns} {...tableRightProps}/>
                    </div>}
                    {!!filterSource.length && displayType === 'single' && (<TargetList {...props} filterSource={filterSource}/>)}
                    {filterSource.length === 0 && displayType === 'single' && <p className={`${prefixCls}-target-nodata`}>
                        <span>{searchValue === '' ? targetNoData : targetNoSearchData}</span>
                    </p>}
                </div>
            </div>
        </Loading>);
};
export default Target;
